import { type FC, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPriorityCell, type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { GenericPerformanceListWithServerPagination } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import type { ColumnOption } from "interfaces/columnOptions";
import { Tooltip } from "components/common/tooltip";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RouteNames } from "enums";
import { TextTruncate } from "components";
import { createTemplatePrioritizedFlowFetcher, getTemplatePrioritizedFlowCards } from "./queries";
import styles from "./templatePrioritizedFlows.module.scss";
import { type PrioritizedFlowTableTemplates } from "../../DataService/types";

type FlowColumnOption = ColumnOption<PrioritizedFlowTableTemplates> & { sortKey?: string };

const columnOptions: FlowColumnOption[] = [
  {
    name: "FLOW NAME",
    width: 6,
    isSortable: true,
    sortKey: "FlowName",
    render: (item: PrioritizedFlowTableTemplates) => {
      return (
        <Tooltip
          target={
            <TextTruncate>
              <MoboLink to={`/${RouteNames.contentFlows}/${item.FlowId}`}>{item.FlowName}</MoboLink>
            </TextTruncate>
          }
          content={item.FlowName}
        />
      );
    },
  },
  {
    name: "PRIORITY",
    width: 3,
    isSortable: true,
    sortKey: "FlowPriority",
    render: (entity: PrioritizedFlowTableTemplates) => {
      return getPriorityCell(entity.FlowPriority);
    },
  },
  {
    name: "CUSTOMERS",
    width: 2,
    isSortable: true,
    sortKey: "Customers",
    render: (item: PrioritizedFlowTableTemplates) => item.Customers.toLocaleString(),
  },
  {
    name: "FLOW STARTS",
    width: 3,
    isSortable: true,
    sortKey: "UsersStarted",
    render: (item: PrioritizedFlowTableTemplates) => item.UsersStarted.toLocaleString(),
  },
  {
    name: "FLOW COMPLETIONS",
    width: 3,
    isSortable: true,
    sortKey: "UsersCompleted",
    render: (item: PrioritizedFlowTableTemplates) => item.UsersCompleted.toLocaleString(),
  },
];

export type Props = {
  templateId: number;
  filter: PerformanceWithAccountFilter;
};

export const TemplatePrioritizedFlows: FC<Props> = ({ templateId, filter }) => {
  const prioritizedFlowCardsQuery = useQuery({
    queryKey: ["templatePrioritizedFlowCards", templateId, filter],
    queryFn: getTemplatePrioritizedFlowCards,
    enabled: filter.type !== undefined,
  });

  const prioritizedFlowTableQueryKey = useMemo(
    () => ["templatePrioritizedFlowTable", templateId, filter] as const,
    [templateId, filter],
  );

  const fetchPrioritizedFlowTable = useMemo(
    () => createTemplatePrioritizedFlowFetcher(templateId, filter),
    [templateId, filter],
  );

  return (
    <div className={styles.contentReport}>
      <div className={styles.cards}>
        <RequestStatusRenderer state={prioritizedFlowCardsQuery.status}>
          {prioritizedFlowCardsQuery.isSuccess && <CardReporting items={prioritizedFlowCardsQuery.data} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.prioritizedFlows}>
        <GenericPerformanceListWithServerPagination
          title="Prioritized Flows"
          columns={columnOptions}
          filter={filter}
          fetchPage={fetchPrioritizedFlowTable}
          queryKeyBase={prioritizedFlowTableQueryKey}
        />
      </div>
    </div>
  );
};
